import { ArrowLeft } from "lucide-react";
import { useEffect, useRef } from "react";
import type { CMSComponentProps } from "../engine/CMSComponent.tsx";
import { formatDate, str, type Post, type PostSummary } from "../types.ts";

/*
 * Blog blocks. The server builds these for /posts, /posts/:slug and /tags/:tag
 * (server/pages.ts); markup and classes are ported from shams-web.
 */

export const PostDate = ({ iso, month = "short" }: { iso: string; month?: "short" | "long" }) => (
  <time dateTime={iso}>{formatDate(iso, month)}</time>
);

const tagHref = (tag: string) => `/tags/${encodeURIComponent(tag)}`;

function PostCard({ post }: { post: PostSummary }) {
  return (
    <a
      href={`/posts/${post.slug}`}
      className="group block rounded-lg border border-border p-5 transition-colors hover:border-foreground/20"
    >
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-semibold text-foreground">{post.title}</h3>
        <div className="shrink-0 text-right">
          <p className="text-xs text-muted-foreground">
            <PostDate iso={post.publishedAt} />
          </p>
          <p className="text-xs text-muted-foreground">{post.readingTime}</p>
        </div>
      </div>
      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
        {post.description}
      </p>
      {post.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {post.tags.map((tag) => (
            <span key={tag} className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {tag}
            </span>
          ))}
        </div>
      )}
    </a>
  );
}

const BackLink = ({ href, label }: { href: string; label: string }) => (
  <a
    href={href}
    className="mb-8 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
  >
    <ArrowLeft className="h-3 w-3" />
    {label}
  </a>
);

/** Post list with optional tag filter links: /posts and /tags/:tag. */
export function PostIndex({ node }: CMSComponentProps) {
  const posts = (node.posts ?? []) as PostSummary[];
  const tags = (node.tags ?? {}) as Record<string, number>;

  return (
    <div className="mx-auto max-w-3xl px-6 pt-28 pb-16">
      {node.backLink === true && <BackLink href="/posts" label="All posts" />}
      <h1 className="text-3xl font-bold text-foreground">{str(node.title)}</h1>

      {Object.keys(tags).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(tags).map(([tag, count]) => (
            <a
              key={tag}
              href={tagHref(tag)}
              className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              {tag} ({count})
            </a>
          ))}
        </div>
      )}

      <div className="mt-8 space-y-4">
        {posts.map((post) => (
          <PostCard key={post.slug} post={post} />
        ))}
      </div>
    </div>
  );
}

/**
 * Renders ```mermaid blocks in the browser. The server leaves their source as
 * text in .mermaid-block, so the page still reads without JavaScript.
 */
function useMermaid(container: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const blocks = [
      ...(container.current?.querySelectorAll<HTMLElement>(".mermaid-block:not([data-rendered])") ?? []),
    ];
    if (blocks.length === 0) return;

    let cancelled = false;
    import("mermaid")
      .then(async ({ default: mermaid }) => {
        mermaid.initialize({
          startOnLoad: false,
          theme: "dark",
          darkMode: true,
          // explicit: "inherit" resolves differently where Mermaid measures labels, clipping them
          fontFamily: getComputedStyle(document.body).fontFamily,
          fontSize: 14,
        });
        for (const [index, block] of blocks.entries()) {
          if (cancelled) return;
          try {
            const { svg } = await mermaid.render(`mermaid-${index}-${Date.now()}`, block.textContent ?? "");
            block.innerHTML = svg;
            block.setAttribute("data-rendered", "");
          } catch (error) {
            console.error("[mermaid]", error);
          }
        }
      })
      .catch((error) => console.error("[mermaid] failed to load", error));

    return () => {
      cancelled = true;
    };
  }, [container]);
}

export function PostArticle({ node }: CMSComponentProps) {
  const post = node.post as Post;
  const body = useRef<HTMLDivElement>(null);
  useMermaid(body);

  return (
    <article className="mx-auto max-w-3xl px-6 pt-28 pb-16">
      <BackLink href="/posts" label="Back to blog" />

      <header className="mb-10">
        <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">{post.title}</h1>
        <div className="mt-3 flex items-center gap-3 text-sm text-muted-foreground">
          <PostDate iso={post.publishedAt} month="long" />
          <span>&middot;</span>
          <span>{post.readingTime}</span>
          {post.author && (
            <>
              <span>&middot;</span>
              <span>{post.author}</span>
            </>
          )}
        </div>
        {post.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {post.tags.map((tag) => (
              <a
                key={tag}
                href={tagHref(tag)}
                className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                {tag}
              </a>
            ))}
          </div>
        )}
      </header>

      {/* rendered from Markdown on the server (server/markdown.ts); raw HTML is stripped there */}
      <div ref={body} className="post-body max-w-none" dangerouslySetInnerHTML={{ __html: post.html }} />
    </article>
  );
}
